import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import type { CanActivateFn, CanMatchFn, UrlTree } from '@angular/router';
import { Router } from '@angular/router';
import { AuthSessionStore } from '@core/state/auth-session.store';
import { SessionService } from '@core/services/authentication/session.service';

const accessChecksInFlight = new Map<string, Promise<boolean | UrlTree>>();

function denyProtectedAccess(
  currentUrl: string,
  authSession: InstanceType<typeof AuthSessionStore>,
  router: Router,
): UrlTree {
  authSession.resolveAccess('unauthenticated', {
    source: 'guard',
    path: currentUrl,
  });
  return router.createUrlTree(['/']);
}

async function evaluateRouteAccess(
  currentUrl: string,
): Promise<boolean | UrlTree> {
  const platformId = inject(PLATFORM_ID);
  const router = inject(Router);
  const authSession = inject(AuthSessionStore);
  const sessionService = inject(SessionService);

  if (!isPlatformBrowser(platformId)) {
    return denyProtectedAccess(currentUrl, authSession, router);
  }

  const inFlight = accessChecksInFlight.get(currentUrl);
  if (inFlight) {
    return inFlight;
  }

  const check = evaluateRouteAccessInBrowser(
    currentUrl,
    authSession,
    sessionService,
    router,
  ).finally(() => {
    accessChecksInFlight.delete(currentUrl);
  });
  accessChecksInFlight.set(currentUrl, check);
  return check;
}

async function evaluateRouteAccessInBrowser(
  currentUrl: string,
  authSession: InstanceType<typeof AuthSessionStore>,
  sessionService: SessionService,
  router: Router,
): Promise<boolean | UrlTree> {
  authSession.beginAccessCheck();
  const allowed = await sessionService.resolveProtectedRouteAccess();

  if (allowed) {
    authSession.resolveAccess('allow');
    return true;
  }

  return denyProtectedAccess(currentUrl, authSession, router);
}

export const authGuard: CanActivateFn = async (_route, state) =>
  evaluateRouteAccess(state.url);

export const authMatchGuard: CanMatchFn = async (_route, segments) => {
  const router = inject(Router);
  const url =
    router.getCurrentNavigation()?.finalUrl?.toString() ??
    `/${segments.map((segment) => segment.path).join('/')}`;
  return evaluateRouteAccess(url);
};
